import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { useCanaryVisibilityScoreTrend } from '@/api/aiVisibility';
import Flare, { Axis, type FlareSeriesOptions, Legend, Line, Tooltip } from '@/components/Flare';
import { Panel } from '@/components/panels';
import { VISUALISATION_CHART_COLORS } from '@/constants/colors';
import { MonthDateFormat, MonthDateYearFormat } from '@/constants/formats';
import { numberFormat } from '@/helper/numberFormatter';

const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
const WINDOW_IN_MS = 90 * 24 * 60 * 60 * 1000;

const AIVisibilityCompetitorTrendChart = () => {
  const { trends, isLoading, error } = useCanaryVisibilityScoreTrend();
  const [windowEnd] = useState(() => Date.now());
  const youLabel = t`You`;
  const { series, colors } = useMemo(() => {
    const allWeeks = trends?.flatMap(({ data }) =>
      data.map(({ sundayOfWeek }) => Date.parse(sundayOfWeek)),
    );
    if (!trends) {
      return { series: undefined, colors: [] as string[] };
    }
    if (!trends.length || !allWeeks?.length) {
      return { series: [] as FlareSeriesOptions[], colors: [] as string[] };
    }

    const firstWeek = Math.min(...allWeeks);
    const lastWeek = Math.max(...allWeeks);
    const weeks: number[] = [];
    for (let week = firstWeek; week <= lastWeek; week += WEEK_IN_MS) {
      weeks.push(week);
    }

    const orderedTrends = [
      ...trends.filter(({ companyType }) => companyType === 'customer'),
      ...trends.filter(({ companyType }) => companyType === 'competitor'),
    ];
    const colors = VISUALISATION_CHART_COLORS;
    const series = orderedTrends.map(({ company, companyType, data }) => {
      const scores = new Map(
        data.map(({ sundayOfWeek, visibilityScore }) => [
          Date.parse(sundayOfWeek),
          visibilityScore,
        ]),
      );
      return {
        id: company,
        name: companyType === 'customer' ? `${company} (${youLabel})` : company,
        data: weeks.map((week) => ({ week, score: scores.get(week) ?? null })),
      };
    });

    return { series, colors };
  }, [trends, youLabel]);

  return (
    <Panel
      title={<Trans>Competitive trend</Trans>}
      subtitle={<Trans>Changes in your competitive visibility over time.</Trans>}
      className="min-h-96"
    >
      <div className="h-72">
        <Flare
          data={series}
          colors={colors}
          isLoading={isLoading}
          error={error}
          emptyMessage={<Trans>No competitor visibility trends available.</Trans>}
          options={{ plotOptions: { series: { connectNulls: false } } }}
        >
          <Legend />
          <Line x="week" y="score" />
          <Axis position="left" min={0} title={t`Visibility Score`} />
          <Axis
            type="datetime"
            position="bottom"
            min={windowEnd - WINDOW_IN_MS}
            max={windowEnd}
            labelFormat={(item) => dayjs(item.value).utc().format(MonthDateFormat)}
          />
          <Tooltip
            shared
            titleFormat={(point) => dayjs(point.x).utc().format(MonthDateYearFormat)}
            rowValueFormat={(point) => numberFormat(point.y, { precision: 1 })}
          />
        </Flare>
      </div>
    </Panel>
  );
};

export default AIVisibilityCompetitorTrendChart;
